// filepath: apps/web/src/features/directory/queries.ts
//
// FR-UI-DIR-1 -- TanStack Query factories + hooks for the People and
// Organization Directory Workspace.
//
// INV-1 (cache-key tenant-scoping): every key factory accepts
// `tenantSlug` as its first argument and includes it as the second
// segment. Switching tenants invalidates the prior tenant's directory
// cache. The static-guard test in `apps/web/src/runtime/staticGuards.test.ts`
// regex-greps `features/directory/**` to assert every query-key factory
// embeds tenantSlug.
//
// INV-7 (OCC): every mutation accepts `row_version` and forwards it
// via the request body; the typed 409 envelope propagates so the
// optimistic-update hook can open the conflict-resolution dialog.
// INV-8 (idempotency): every mutation carries `X-Idempotency-Key`.
//
// F1: `/api/directory/teams` and `/api/directory/qualifications` are
// NOT shipped. The teams / qualifications pages must NEVER call those
// endpoints -- the static-guard test asserts the absence (no fetch
// call, no import of the not-yet-existing route).

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiError } from "@/definitions/types";
import { apiFetch } from "@/api";
import type {
  DirectoryAuditEntry,
  DirectoryAuditFilterState,
  DirectoryBackendError,
  DirectoryConflictEnvelope,
  DirectoryConflictError,
  DirectoryFetchState,
  DirectoryListResponse,
  EmploymentAssignment,
  LegalEntity,
  Location,
  LocationsFilterState,
  OrganizationUnit,
  PeopleFilterState,
  Person,
  PersonSummary,
  PositionsFilterState,
  Position,
} from "./types";

// ─── Cache Key Factories (INV-1: tenantSlug as 2nd segment) ──────────────────

export const directoryKeys = {
  /** Root key for the directory subtree. */
  all: ["directory"] as const,
  /** People list. */
  peopleList: (tenantSlug: string, filter: PeopleFilterState) =>
    [
      ...directoryKeys.all,
      tenantSlug,
      "people",
      JSON.stringify(filter),
    ] as const,
  /** Person detail composite. */
  person: (tenantSlug: string, personId: string) =>
    [...directoryKeys.all, tenantSlug, "person", personId] as const,
  /** Organization unit list. */
  orgUnitList: (tenantSlug: string) =>
    [...directoryKeys.all, tenantSlug, "organization-units"] as const,
  /** Legal entity list. */
  legalEntityList: (tenantSlug: string) =>
    [...directoryKeys.all, tenantSlug, "legal-entities"] as const,
  /** Position list. */
  positionsList: (tenantSlug: string, filter: PositionsFilterState) =>
    [
      ...directoryKeys.all,
      tenantSlug,
      "positions",
      JSON.stringify(filter),
    ] as const,
  /** Employment assignment list (by person). */
  employmentByPerson: (tenantSlug: string, personId: string) =>
    [...directoryKeys.all, tenantSlug, "employment", personId] as const,
  /** Location list. */
  locationsList: (tenantSlug: string, filter: LocationsFilterState) =>
    [
      ...directoryKeys.all,
      tenantSlug,
      "locations",
      JSON.stringify(filter),
    ] as const,
  /** Directory audit feed. */
  audit: (tenantSlug: string, filter: DirectoryAuditFilterState) =>
    [
      ...directoryKeys.all,
      tenantSlug,
      "audit",
      JSON.stringify(filter),
    ] as const,
};

// ─── RFC3339 Client-Side Validation (audit feature pattern) ──────────────────

function isRFC3339(s: string): boolean {
  if (!s) return false;
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/.test(
    s,
  );
}

function isISODate(s: string): boolean {
  if (!s) return false;
  return /^\d{4}-\d{2}-\d{2}$/.test(s);
}

// ─── Generic Query-Hook Result Shape ──────────────────────────────────────────

export interface DirectoryQueryResult<T> {
  data: T | null;
  state: DirectoryFetchState;
  error: DirectoryBackendError | null;
}

// ─── Classifier (mirrors audit feature pattern) ──────────────────────────────

function classifyResponse(
  res: Response,
): { ok: true } | { ok: false; error: DirectoryBackendError } {
  if (res.status === 200 || res.status === 201) return { ok: true };
  if (res.status === 403 || res.status === 404) {
    return {
      ok: false,
      error: {
        kind: "permission-denied",
        message: res.statusText,
        retriable: false,
      },
    };
  }
  if (res.status === 422) {
    return {
      ok: false,
      error: {
        kind: "validation-error",
        message: res.statusText,
        retriable: false,
      },
    };
  }
  if (res.status === 429) {
    const retryAfterHeader = res.headers.get("Retry-After");
    const retryAfterSeconds = retryAfterHeader
      ? Number(retryAfterHeader)
      : undefined;
    return {
      ok: false,
      error: {
        kind: "rate-limit",
        message: res.statusText,
        retriable: true,
        ...(retryAfterSeconds !== undefined &&
        Number.isFinite(retryAfterSeconds)
          ? { retryAfterSeconds }
          : {}),
      },
    };
  }
  return {
    ok: false,
    error: {
      kind: "fetch-failure",
      message: res.statusText,
      retriable: true,
    },
  };
}

// ─── Query Param Builders ────────────────────────────────────────────────────

function peopleFilterQuery(filter: PeopleFilterState): string {
  const p = new URLSearchParams();
  if (filter.query) p.set("q", filter.query);
  if (filter.status !== "all") p.set("status", filter.status);
  if (filter.organization_unit_id)
    p.set("organization_unit_id", filter.organization_unit_id);
  if (filter.cursor) p.set("cursor", filter.cursor);
  p.set("limit", "50");
  return p.toString();
}

function positionsFilterQuery(filter: PositionsFilterState): string {
  const p = new URLSearchParams();
  if (filter.organization_unit_id)
    p.set("organization_unit_id", filter.organization_unit_id);
  if (filter.status !== "all") p.set("status", filter.status);
  if (filter.cursor) p.set("cursor", filter.cursor);
  p.set("limit", "50");
  return p.toString();
}

function locationsFilterQuery(filter: LocationsFilterState): string {
  const p = new URLSearchParams();
  // ISS-515 BA option (a): country_code filter is removed entirely
  // (Go's list handler doesn't accept it; the pre-ISS-515 SPA was
  // passing a no-op ?country_code=<value> the server ignored).
  if (filter.status !== "all") p.set("status", filter.status);
  if (filter.parent_location_id)
    p.set("parent_location_id", filter.parent_location_id);
  if (filter.include_closed) p.set("include_closed", "true");
  if (filter.cursor) p.set("cursor", filter.cursor);
  p.set("limit", "50");
  return p.toString();
}

function auditFilterQuery(filter: DirectoryAuditFilterState): string {
  const p = new URLSearchParams();
  if (filter.resource_kind !== "all")
    p.set("resource_kind", filter.resource_kind);
  if (filter.after) {
    if (!isRFC3339(filter.after)) {
      // Reject without server roundtrip (AL-7 audit pattern).
      return "?invalid_after=1";
    }
    p.set("after", filter.after);
  }
  if (filter.before) {
    if (!isRFC3339(filter.before)) {
      return "?invalid_before=1";
    }
    p.set("before", filter.before);
  }
  if (filter.cursor) p.set("cursor", filter.cursor);
  p.set("limit", "50");
  return p.toString();
}

// ─── People Directory ────────────────────────────────────────────────────────

/**
 * usePeopleList -- GET /api/people?... → DirectoryListResponse<Person>.
 * Reads the person list with filter / pagination.
 */
export function usePeopleList(
  filter: PeopleFilterState,
): DirectoryQueryResult<DirectoryListResponse<Person>> {
  const { tenantSlug } = useTenant();
  const query = useQuery<DirectoryListResponse<Person>, ApiError>({
    queryKey: directoryKeys.peopleList(tenantSlug, filter),
    queryFn: async () => {
      const qs = peopleFilterQuery(filter);
      const r = await apiFetch(`/api/people?${qs}`);
      if (!r.ok) throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      return (await r.json()) as DirectoryListResponse<Person>;
    },
  });

  return toQueryResult<DirectoryListResponse<Person>>(query);
}

/**
 * usePersonSummary -- composes the four sub-resources for the
 * /people/:id route. Each sub-resource is fetched in parallel inside
 * the queryFn; any single failure propagates as a typed ApiError.
 */
export function usePersonSummary(
  personId: string,
): DirectoryQueryResult<PersonSummary> {
  const { tenantSlug } = useTenant();
  const query = useQuery<PersonSummary, ApiError>({
    queryKey: directoryKeys.person(tenantSlug, personId),
    queryFn: async (): Promise<PersonSummary> => {
      const [personRes, contactsRes, employmentRes, auditRes] =
        await Promise.all([
          apiFetch(`/api/people/${encodeURIComponent(personId)}`),
          apiFetch(`/api/people/${encodeURIComponent(personId)}/contacts`),
          apiFetch(
            `/api/directory/employment-assignments/by-person/${encodeURIComponent(personId)}`,
          ),
          apiFetch(`/api/people/${encodeURIComponent(personId)}/audit`),
        ]);
      for (const r of [personRes, contactsRes, employmentRes, auditRes]) {
        if (!r.ok)
          throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      const [person, contacts, employment, audit] = await Promise.all([
        personRes.json() as Promise<Person>,
        contactsRes.json() as Promise<{
          items: import("./types").PersonContact[];
        }>,
        employmentRes.json() as Promise<
          DirectoryListResponse<EmploymentAssignment>
        >,
        auditRes.json() as Promise<{
          items: import("./types").PersonAuditEntry[];
        }>,
      ]);
      // INV-2: server-stripped fields are NEVER rebuilt client-side;
      // qualifications are part of the deferred F1 surface (no endpoint
      // exists yet) -- emit an empty array and rely on the
      // QualificationsPage / PeopleDirectoryPage UX to render the
      // fetch-failure state for that sub-resource.
      return {
        person,
        contacts: contacts.items,
        employment: employment.items,
        qualifications: [],
        audit: audit.items,
        // Display helpers copied straight from the server-stripped
        // Person record (FR-AUTH-7 — never synthesised client-side
        // per INV-2). ISS-518 BA option (a): the pre-ISS-518
        // denormalized helpers (title, primary_unit_name, tags) +
        // the primary_*_id family were DROPPED — Go never emitted
        // them and the spec's "No new backend" forecloses them.
        display_name: person.display_name,
        status: person.status,
      };
    },
    enabled: Boolean(personId),
  });

  return toQueryResult<PersonSummary>(query);
}

// ─── Organization ────────────────────────────────────────────────────────────

/**
 * useOrganizationUnitList -- GET /api/directory/organization-units →
 * DirectoryListResponse<OrganizationUnit>. Used by the organization
 * tree page (rendered as a recursive tree client-side).
 */
export function useOrganizationUnitList(): DirectoryQueryResult<
  DirectoryListResponse<OrganizationUnit>
> {
  const { tenantSlug } = useTenant();
  const query = useQuery<DirectoryListResponse<OrganizationUnit>, ApiError>({
    queryKey: directoryKeys.orgUnitList(tenantSlug),
    queryFn: async () => {
      const r = await apiFetch(`/api/directory/organization-units`);
      if (!r.ok) throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      return (await r.json()) as DirectoryListResponse<OrganizationUnit>;
    },
  });
  return toQueryResult<DirectoryListResponse<OrganizationUnit>>(query);
}

/**
 * useLegalEntityList -- GET /api/directory/legal-entities → DirectoryListResponse<LegalEntity>.
 * Used by the org tree to render legal-entity headers.
 */
export function useLegalEntityList(): DirectoryQueryResult<
  DirectoryListResponse<LegalEntity>
> {
  const { tenantSlug } = useTenant();
  const query = useQuery<DirectoryListResponse<LegalEntity>, ApiError>({
    queryKey: directoryKeys.legalEntityList(tenantSlug),
    queryFn: async () => {
      const r = await apiFetch(`/api/directory/legal-entities`);
      if (!r.ok) throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      return (await r.json()) as DirectoryListResponse<LegalEntity>;
    },
  });
  return toQueryResult<DirectoryListResponse<LegalEntity>>(query);
}

// ─── Positions ────────────────────────────────────────────────────────────────

/**
 * usePositionsList -- GET /api/directory/positions?... → DirectoryListResponse<Position>.
 */
export function usePositionsList(
  filter: PositionsFilterState,
): DirectoryQueryResult<DirectoryListResponse<Position>> {
  const { tenantSlug } = useTenant();
  const query = useQuery<DirectoryListResponse<Position>, ApiError>({
    queryKey: directoryKeys.positionsList(tenantSlug, filter),
    queryFn: async () => {
      const qs = positionsFilterQuery(filter);
      const r = await apiFetch(`/api/directory/positions?${qs}`);
      if (!r.ok) throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      return (await r.json()) as DirectoryListResponse<Position>;
    },
  });
  return toQueryResult<DirectoryListResponse<Position>>(query);
}

// ─── Employment Timeline ─────────────────────────────────────────────────────

/**
 * useEmploymentByPerson -- GET /api/directory/employment-assignments/by-person/{id}
 * → DirectoryListResponse<EmploymentAssignment>.
 */
export function useEmploymentByPerson(
  personId: string,
): DirectoryQueryResult<DirectoryListResponse<EmploymentAssignment>> {
  const { tenantSlug } = useTenant();
  const query = useQuery<DirectoryListResponse<EmploymentAssignment>, ApiError>(
    {
      queryKey: directoryKeys.employmentByPerson(tenantSlug, personId),
      queryFn: async () => {
        const r = await apiFetch(
          `/api/directory/employment-assignments/by-person/${encodeURIComponent(personId)}`,
        );
        if (!r.ok)
          throw new ApiError(r.statusText, r.status, `http_${r.status}`);
        return (await r.json()) as DirectoryListResponse<EmploymentAssignment>;
      },
      enabled: Boolean(personId),
    },
  );
  return toQueryResult<DirectoryListResponse<EmploymentAssignment>>(query);
}

// ─── Locations ───────────────────────────────────────────────────────────────

/**
 * useLocationsList -- GET /api/directory/locations?... → DirectoryListResponse<Location>.
 */
export function useLocationsList(
  filter: LocationsFilterState,
): DirectoryQueryResult<DirectoryListResponse<Location>> {
  const { tenantSlug } = useTenant();
  const query = useQuery<DirectoryListResponse<Location>, ApiError>({
    queryKey: directoryKeys.locationsList(tenantSlug, filter),
    queryFn: async () => {
      const qs = locationsFilterQuery(filter);
      const r = await apiFetch(`/api/directory/locations?${qs}`);
      if (!r.ok) throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      return (await r.json()) as DirectoryListResponse<Location>;
    },
  });
  return toQueryResult<DirectoryListResponse<Location>>(query);
}

// ─── Directory Audit ─────────────────────────────────────────────────────────

/**
 * useDirectoryAudit -- GET /api/people/audit?... → DirectoryAuditEntry[].
 * Reuses the FR-DIR-1 audit feed for the directory workspace's
 * audit-history surface.
 */
export function useDirectoryAudit(
  filter: DirectoryAuditFilterState,
): DirectoryQueryResult<DirectoryListResponse<DirectoryAuditEntry>> {
  const { tenantSlug } = useTenant();
  const query = useQuery<DirectoryListResponse<DirectoryAuditEntry>, ApiError>({
    queryKey: directoryKeys.audit(tenantSlug, filter),
    queryFn: async () => {
      const qs = auditFilterQuery(filter);
      if (qs.includes("invalid_")) {
        // Client-side RFC3339 validation rejects without server
        // roundtrip (mirrors the audit feature AL-7).
        throw new ApiError("invalid after/before", 422, "validation_error");
      }
      const r = await apiFetch(`/api/people/audit?${qs}`);
      if (!r.ok) throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      return (await r.json()) as DirectoryListResponse<DirectoryAuditEntry>;
    },
  });
  return toQueryResult<DirectoryListResponse<DirectoryAuditEntry>>(query);
}

// ─── Mutations (INV-7 + INV-8) ────────────────────────────────────────────────

interface MutationCommon<TRecord extends { id: string; row_version: number }> {
  record: TRecord;
  /** Optional client-side note attached to the audit emission (audit-only). */
  reason?: string;
}

/**
 * useUpdatePerson -- PATCH /api/people/{id}.
 *
 * Carries `row_version` in the body (INV-7) and `X-Idempotency-Key`
 * in the headers (INV-8). On 409, throws a typed
 * DirectoryConflictError so the optimistic-update hook can open the
 * conflict-resolution dialog.
 */
export function useUpdatePerson() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<Person, Error, MutationCommon<Person>>({
    mutationFn: async ({ record, reason }) => {
      const { id, row_version, ...patch } = record;
      const res = await apiFetch(`/api/people/${encodeURIComponent(id)}`, {
        method: "PATCH",
        init: {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...patch,
            row_version,
            idempotency_key: crypto.randomUUID(),
            reason: reason ?? "",
          }),
        },
        idempotencyKey: true,
      });
      if (res.status === 409) {
        const envelope = (await res
          .json()
          .catch(() => ({}))) as Partial<DirectoryConflictEnvelope>;
        throw {
          kind: "directory_conflict",
          envelope: {
            code: "row_version_mismatch",
            message: envelope.message ?? "row_version_mismatch",
            client_version: envelope.client_version ?? row_version ?? 0,
            current_version: envelope.current_version ?? 0,
            server_record: envelope.server_record ?? null,
          },
          resource_kind: "person",
          resource_id: id,
        } satisfies DirectoryConflictError;
      }
      if (!res.ok)
        throw new ApiError(res.statusText, res.status, `http_${res.status}`);
      return (await res.json()) as Person;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: directoryKeys.person(tenantSlug, variables.record.id),
      });
      queryClient.invalidateQueries({
        queryKey: directoryKeys.peopleList(tenantSlug, {
          query: "",
          status: "all",
          organization_unit_id: null,
          cursor: null,
        }),
      });
      // BA contract (uat-ba note_13124): disarm after success.
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}

/**
 * useUpdateOrganizationUnit -- PATCH /api/directory/organization-units/{id}.
 */
export function useUpdateOrganizationUnit() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<OrganizationUnit, Error, MutationCommon<OrganizationUnit>>(
    {
      mutationFn: async ({ record, reason }) => {
        const { id, row_version, ...patch } = record;
        const res = await apiFetch(
          `/api/directory/organization-units/${encodeURIComponent(id)}`,
          {
            method: "PATCH",
            init: {
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                ...patch,
                row_version,
                idempotency_key: crypto.randomUUID(),
                reason: reason ?? "",
              }),
            },
            idempotencyKey: true,
          },
        );
        if (res.status === 409) {
          const envelope = (await res
            .json()
            .catch(() => ({}))) as Partial<DirectoryConflictEnvelope>;
          throw {
            kind: "directory_conflict",
            envelope: {
              code: "row_version_mismatch",
              message: envelope.message ?? "row_version_mismatch",
              client_version: envelope.client_version ?? row_version ?? 0,
              current_version: envelope.current_version ?? 0,
              server_record: envelope.server_record ?? null,
            },
            resource_kind: "organization_unit",
            resource_id: id,
          } satisfies DirectoryConflictError;
        }
        if (!res.ok)
          throw new ApiError(res.statusText, res.status, `http_${res.status}`);
        return (await res.json()) as OrganizationUnit;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: directoryKeys.orgUnitList(tenantSlug),
        });
        // BA contract (uat-ba note_13124): disarm after success.
      },
      onError: () => {
        // BA contract (uat-ba note_13124): disarm on failure.
      },
      onSettled: () => {
        // Belt-and-braces; both branches above already disarm.
      },
    },
  );
}

/**
 * useMoveOrganizationUnit -- POST /api/directory/organization-units/{id}/move.
 *
 * ISS-517. Reparenting has its OWN route on the Go side
 * (`internal/directory/organization_unit/handlers.go:156-182`); the
 * PATCH route cannot move a unit. The body is exactly
 * `{row_version, parent_organization_unit_id}` — no `fields`
 * wrapper (`handlers.go:151-154`).
 *
 * INV-7: carries `row_version` in the body.
 * INV-8: carries a fresh `X-Idempotency-Key`.
 *
 * BA ruling note 18333 (D2) — CYCLE SAFETY IS SERVER-AUTHORITATIVE.
 * The server runs a recursive CTE (`repository.go:165-203`) and
 * answers an illegal reparent with 409 + `code: "cycle_detected"`
 * (`handlers.go:331-332`). This hook performs NO client-side
 * descendant walk and NO client-side cycle test; it re-throws the
 * server's verdict as an ApiError with that code so the page can map
 * it to the parent-picker validation slot. A 409 whose code is
 * `cycle_detected` must therefore NOT be folded into the typed
 * DirectoryConflictError — only `row_version_mismatch` is a version
 * conflict.
 */
export interface MoveOrganizationUnitVars {
  id: string;
  parent_organization_unit_id: string | null;
  row_version: number;
}

export function useMoveOrganizationUnit() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<OrganizationUnit, Error, MoveOrganizationUnitVars>({
    mutationFn: async ({ id, parent_organization_unit_id, row_version }) => {
      const res = await apiFetch(
        `/api/directory/organization-units/${encodeURIComponent(id)}/move`,
        {
          method: "POST",
          init: {
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              row_version,
              parent_organization_unit_id,
            }),
          },
          idempotencyKey: true,
        },
      );
      if (res.status === 409) {
        // `code` is widened to `string` because the move route can
        // answer 409 with either 'row_version_mismatch' OR
        // 'cycle_detected' (`handlers.go:325-338`); the
        // DirectoryConflictEnvelope literal union models only the
        // former, so it is omitted before the widening.
        const envelope = (await res.json().catch(() => ({}))) as Omit<
          Partial<DirectoryConflictEnvelope>,
          "code"
        > & { code?: string };
        if (envelope.code === "cycle_detected") {
          // Server-authoritative cycle verdict (BA ruling D2). Surface
          // it verbatim — the caller renders it as a parent-picker
          // validation error.
          throw new ApiError(
            envelope.message ?? "cycle_detected",
            409,
            "cycle_detected",
          );
        }
        throw {
          kind: "directory_conflict",
          envelope: {
            code: "row_version_mismatch",
            message: envelope.message ?? "row_version_mismatch",
            client_version: envelope.client_version ?? row_version ?? 0,
            current_version: envelope.current_version ?? 0,
            server_record: envelope.server_record ?? null,
          },
          resource_kind: "organization_unit",
          resource_id: id,
        } satisfies DirectoryConflictError;
      }
      if (!res.ok)
        throw new ApiError(res.statusText, res.status, `http_${res.status}`);
      return (await res.json()) as OrganizationUnit;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: directoryKeys.orgUnitList(tenantSlug),
      });
    },
  });
}

/**
 * useArchiveOrganizationUnit -- POST
 * /api/directory/organization-units/{id}/close.
 *
 * ISS-517. Archive is the Go `close` verb (NOT a DELETE — the Go
 * handler set mounts only POST /{prefix}/{id}/close,
 * `handlers.go:48`). It sets status='closed' + closed_at=now()
 * (`repository.go:206-222`) and returns 204 No Content.
 *
 * INV-7: carries `row_version` in the body; a stale submission
 * returns 409 + code='row_version_mismatch' as a typed
 * DirectoryConflictError.
 * INV-8: carries a fresh `X-Idempotency-Key`.
 */
export interface ArchiveOrganizationUnitVars {
  id: string;
  row_version: number;
}

export function useArchiveOrganizationUnit() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<void, Error, ArchiveOrganizationUnitVars>({
    mutationFn: async ({ id, row_version }) => {
      const res = await apiFetch(
        `/api/directory/organization-units/${encodeURIComponent(id)}/close`,
        {
          method: "POST",
          init: {
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ row_version }),
          },
          idempotencyKey: true,
        },
      );
      if (res.status === 409) {
        const envelope = (await res
          .json()
          .catch(() => ({}))) as Partial<DirectoryConflictEnvelope>;
        throw {
          kind: "directory_conflict",
          envelope: {
            code: "row_version_mismatch",
            message: envelope.message ?? "row_version_mismatch",
            client_version: envelope.client_version ?? row_version ?? 0,
            current_version: envelope.current_version ?? 0,
            server_record: envelope.server_record ?? null,
          },
          resource_kind: "organization_unit",
          resource_id: id,
        } satisfies DirectoryConflictError;
      }
      if (res.status === 204) return;
      if (!res.ok)
        throw new ApiError(res.statusText, res.status, `http_${res.status}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: directoryKeys.orgUnitList(tenantSlug),
      });
    },
  });
}

// ─── Person-scoped Hooks (FR-UI-DIR-1 AC-2) ──────────────────────────────────

/**
 * usePersonEmployment -- person-scoped read of the employment
 * assignment feed. Identical wire to useEmploymentByPerson; named
 * separately so the PersonSummaryPage can call it without dragging
 * in a list-shaped filter arg.
 */
export function usePersonEmployment(
  personId: string,
): DirectoryQueryResult<DirectoryListResponse<EmploymentAssignment>> {
  return useEmploymentByPerson(personId);
}

/**
 * usePersonAudit -- person-scoped read of the audit feed. Calls the
 * same /api/directory/audit endpoint as the global feed, but
 * pre-filters by person_id.
 */
export function usePersonAudit(
  personId: string,
): DirectoryQueryResult<DirectoryListResponse<DirectoryAuditEntry>> {
  const { tenantSlug } = useTenant();
  const query = useQuery<DirectoryListResponse<DirectoryAuditEntry>, ApiError>({
    queryKey: [
      ...directoryKeys.all,
      tenantSlug,
      "person-audit",
      personId,
    ] as const,
    queryFn: async () => {
      const qs = `resource_kind=person&resource_id=${encodeURIComponent(personId)}&limit=50`;
      const r = await apiFetch(`/api/directory/audit?${qs}`);
      if (!r.ok) throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      return (await r.json()) as DirectoryListResponse<DirectoryAuditEntry>;
    },
    enabled: Boolean(personId),
  });
  return toQueryResult<DirectoryListResponse<DirectoryAuditEntry>>(query);
}

/**
 * usePersonQualifications -- per-person qualifications feed.
 *
 * F1 FINDING: the per-person qualifications endpoint is NOT shipped.
 * This hook returns the deferred state immediately and MUST NOT call
 * the missing endpoint. The static-guard test asserts no fetch to
 * /api/directory/qualifications and no import of that route.
 */
export function usePersonQualifications(
  _personId: string,
): DirectoryQueryResult<DirectoryListResponse<unknown>> {
  return {
    data: null,
    state: "fetch-failure",
    error: {
      kind: "fetch-failure",
      message: "qualifications_deferred",
      retriable: false,
    },
  };
}

/**
 * useDirectoryAuditFeed -- tenant-wide directory audit feed.
 * Alias of useDirectoryAudit; named separately so the
 * AuditHistoryPage can call it explicitly.
 */
export function useDirectoryAuditFeed(
  filter: DirectoryAuditFilterState,
): DirectoryQueryResult<DirectoryListResponse<DirectoryAuditEntry>> {
  return useDirectoryAudit(filter);
}

/**
 * useOrganizationUnits -- alias of useOrganizationUnitList.
 */
export function useOrganizationUnits(): DirectoryQueryResult<
  DirectoryListResponse<OrganizationUnit>
> {
  return useOrganizationUnitList();
}

/**
 * useOrganizationUnit -- single org-unit fetch by id. Matches the
 * internal/directory wire shape and is used by the org tree when a
 * node is expanded for the first time.
 */
export function useOrganizationUnit(
  id: string,
): DirectoryQueryResult<OrganizationUnit> {
  const { tenantSlug } = useTenant();
  const query = useQuery<OrganizationUnit, ApiError>({
    queryKey: [
      ...directoryKeys.all,
      tenantSlug,
      "organization-unit",
      id,
    ] as const,
    queryFn: async () => {
      const r = await apiFetch(
        `/api/directory/organization-units/${encodeURIComponent(id)}`,
      );
      if (!r.ok) throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      return (await r.json()) as OrganizationUnit;
    },
    enabled: Boolean(id),
  });
  return toQueryResult<OrganizationUnit>(query);
}

/**
 * useLegalEntities -- alias of useLegalEntityList.
 */
export function useLegalEntities(): DirectoryQueryResult<
  DirectoryListResponse<LegalEntity>
> {
  return useLegalEntityList();
}

/**
 * useLegalEntity -- single legal-entity fetch by id.
 */
export function useLegalEntity(id: string): DirectoryQueryResult<LegalEntity> {
  const { tenantSlug } = useTenant();
  const query = useQuery<LegalEntity, ApiError>({
    queryKey: [...directoryKeys.all, tenantSlug, "legal-entity", id] as const,
    queryFn: async () => {
      const r = await apiFetch(
        `/api/directory/legal-entities/${encodeURIComponent(id)}`,
      );
      if (!r.ok) throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      return (await r.json()) as LegalEntity;
    },
    enabled: Boolean(id),
  });
  return toQueryResult<LegalEntity>(query);
}

/**
 * useLocations -- alias of useLocationsList.
 */
export function useLocations(
  filter: LocationsFilterState,
): DirectoryQueryResult<DirectoryListResponse<Location>> {
  return useLocationsList(filter);
}

/**
 * useLocation -- single location fetch by id.
 */
export function useLocation(id: string): DirectoryQueryResult<Location> {
  const { tenantSlug } = useTenant();
  const query = useQuery<Location, ApiError>({
    queryKey: [...directoryKeys.all, tenantSlug, "location", id] as const,
    queryFn: async () => {
      const r = await apiFetch(
        `/api/directory/locations/${encodeURIComponent(id)}`,
      );
      if (!r.ok) throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      return (await r.json()) as Location;
    },
    enabled: Boolean(id),
  });
  return toQueryResult<Location>(query);
}

/**
 * usePositions -- alias of usePositionsList.
 */
export function usePositions(
  filter: PositionsFilterState,
): DirectoryQueryResult<DirectoryListResponse<Position>> {
  return usePositionsList(filter);
}

/**
 * usePosition -- single position fetch by id.
 */
export function usePosition(id: string): DirectoryQueryResult<Position> {
  const { tenantSlug } = useTenant();
  const query = useQuery<Position, ApiError>({
    queryKey: [...directoryKeys.all, tenantSlug, "position", id] as const,
    queryFn: async () => {
      const r = await apiFetch(
        `/api/directory/positions/${encodeURIComponent(id)}`,
      );
      if (!r.ok) throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      return (await r.json()) as Position;
    },
    enabled: Boolean(id),
  });
  return toQueryResult<Position>(query);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * toQueryResult -- collapse a TanStack `useQuery` into the directory
 * shape so pages can render by state name (mirrors audit feature).
 */
function toQueryResult<T>(
  query: UseQueryResult<T, ApiError>,
): DirectoryQueryResult<T> {
  if (query.isLoading) {
    return { data: null, state: "loading", error: null };
  }
  if (query.isError) {
    const err = query.error;
    if (err.status === 403) {
      return {
        data: null,
        state: "permission-denied",
        error: {
          kind: "permission-denied",
          message: err.message,
          retriable: false,
        },
      };
    }
    if (err.status === 422) {
      return {
        data: null,
        state: "validation-error",
        error: {
          kind: "validation-error",
          message: err.message,
          retriable: false,
        },
      };
    }
    if (err.status === 429) {
      return {
        data: null,
        state: "rate-limit",
        error: {
          kind: "rate-limit",
          message: err.message,
          retriable: true,
          ...(err.retryAfterSeconds !== null
            ? { retryAfterSeconds: err.retryAfterSeconds }
            : {}),
        },
      };
    }
    return {
      data: null,
      state: "fetch-failure",
      error: { kind: "fetch-failure", message: err.message, retriable: true },
    };
  }
  return { data: query.data ?? null, state: "success", error: null };
}

export const __test__ = {
  classifyResponse,
  isRFC3339,
  isISODate,
  peopleFilterQuery,
  positionsFilterQuery,
  locationsFilterQuery,
  auditFilterQuery,
};
